import type { MePayload } from '../api/me.ts';

type MfaFactor = 'totp' | 'email_otp';

/**
 * Which MFA screen and form an account needs. The factor the account HOLDS
 * (`activeMfaFactor`, the server's enforcedFactor) wins; the recommendation only
 * chooses what an account holding none sets up. The only reader of those /me
 * fields (ESLint `no-restricted-syntax`).
 */
export type MfaStep =
  | { readonly kind: 'upgrade' }
  | { readonly kind: 'enrol'; readonly factor: MfaFactor }
  | { readonly kind: 'held'; readonly factor: MfaFactor };

export function mfaStep(me: MePayload): MfaStep {
  if (me.mfaUpgradeRequired) return { kind: 'upgrade' };
  if (me.activeMfaFactor !== null) return { kind: 'held', factor: me.activeMfaFactor };
  return { kind: 'enrol', factor: me.recommendedMfaFactor };
}

/** Entering an emailed code is the same act at enrolment and at sign-in, so only a
 *  held authenticator verifies on /mfa; sending it to setup would offer to REPLACE it. */
export function mfaPath(step: MfaStep): '/mfa' | '/mfa/setup' {
  return step.kind === 'held' && step.factor === 'totp' ? '/mfa' : '/mfa/setup';
}

export function holdsAuthenticator(me: MePayload): boolean {
  const step = mfaStep(me);
  return step.kind === 'held' && step.factor === 'totp';
}

/** The link an emailed-code account follows to switch to the app before it must (the 14-day
 *  grace, owner 2026-10-27); /mfa/setup reads `switch=app`. */
export const SWITCH_TO_APP_PATH = '/mfa/setup?switch=app';

/** The date the app becomes required for an account still on the emailed code inside its grace;
 *  null once required (the gate takes over) or when it never will be. */
export function authenticatorDueAt(me: MePayload, now: number = Date.now()): number | null {
  const step = mfaStep(me);
  // The pre-MFA profile does not carry it: nothing is due before the session is verified.
  const due = 'mfaUpgradeDueAt' in me ? me.mfaUpgradeDueAt : null;
  return step.kind === 'held' && step.factor === 'email_otp' && due !== null && due > now ? due : null;
}
